import { Typography, makeStyles, SvgIcon } from "@material-ui/core";
import React from "react";
import clsx from "clsx";
import { secondsToTime } from "../../utils/helpers";
import OptimizedTooltip from "../common/OptimizedTooltip";

import LateIcon from "@material-ui/icons/ErrorOutline";
import SoonerIcon from "@material-ui/icons/History";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";

import { ReactComponent as StickyNote } from "../../assets/images/icons/sticky-note.svg";

const useStyles = makeStyles(theme => ({
  stopInfoWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    "& .stop-status-icon": {
      fontSize: "1.25rem",
      color: "rgba(0, 0, 0, 0.54)",
      marginRight: "0.25rem",
      "&--skipped": {
        color: theme.palette.error.main
      },
      "&--done": {
        color: theme.palette.success.main
      },
      "&--late": {
        color: theme.palette.warning.main
      },
      "&--note:hover": {
        filter: "brightness(0.1)"
      }
    },
    "&.stop-late .stop-status-icon": {
      color: "#FEB700"
    }
  },
  text13: {
    fontSize: "0.8125rem"
  },
  stopInfoTimeLabel: {
    padding: "1px 0.25rem",
    lineHeight: "1rem",
    borderRadius: "0.25rem"
  },
  stopInfoTimeLabelDone: {
    color: theme.palette.success.main,
    backgroundColor: theme.palette.success.secondary,
    border: `1px solid ${theme.palette.success.thirdary}`
  },
  stopInfoTimeLabelSkipped: {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.secondary,
    border: `1px solid ${theme.palette.error.thirdary}`
  },
  noteByDriver: {
    width: "248px",
    background: "#606165",
    padding: "0.666rem 0.75rem",
    boxShadow: "0.125rem 0 0.75rem rgba(0, 0, 0, 0.24)",
    borderRadius: "0.25rem"
  },
  noteByDriverTitle: {
    fontFamily: "Roboto",
    fontStyle: "normal",
    fontWeight: 500,
    fontSize: "0.825rem",
    lineHeight: "1rem",
    color: "#FFFFFF"
  },
  noteByDriverText: {
    paddingTop: "0.25rem",
    fontFamily: "Roboto",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "0.825rem",
    lineHeight: "1rem",
    color: "#FFFFFF"
  }
}));

export default function StopTimeInfo({
  unassigned,
  stop,
  routeStatus,
  liveStatusUpdates,
  showAll
}) {
  const classes = useStyles();

  // Used when live updates are disabled
  const hideDetails =
    routeStatus === "in_progress" &&
    !liveStatusUpdates &&
    stop.type !== "start";

  // Arrival and end times
  const arr_time = hideDetails ? stop.original_arr_time : stop.arr_time;
  const end_time = hideDetails ? stop.original_end_time : stop.end_time;

  const timeWindowEarliest =
    stop.time_windows && stop.time_windows.length
      ? stop.time_windows[0].earliest
      : showAll
      ? stop.time_window_earliest
      : null;
  const timeWindowLatest =
    stop.time_windows && stop.time_windows.length
      ? stop.time_windows[0].latest
      : showAll
      ? stop.time_window_latest
      : null;

  const isLate =
    arr_time !== null && timeWindowLatest && arr_time > timeWindowLatest;

  const isEarly =
    arr_time !== null &&
    timeWindowEarliest &&
    stop.type !== "start" &&
    stop.type !== "end" &&
    arr_time < timeWindowEarliest;

  const isDone = !unassigned && arr_time && "finished" === stop.status;
  const isSkipped = !unassigned && arr_time && "skipped" === stop.status;

  const hasNoteByDriver = !!(
    (!unassigned && (stop.recipient_note || stop.completion_note)) ||
    ""
  );

  const noTimeWindow =
    !(timeWindowEarliest && timeWindowLatest) ||
    (timeWindowEarliest === 0 && timeWindowLatest >= 86340);

  return (
    <>
      <Typography
        component="span"
        variant="body2"
        className={clsx(`stop-on-time`, classes.stopInfoWrapper)}
      >
        {!!hasNoteByDriver && (
          <OptimizedTooltip
            placement="bottom"
            classes={{ tooltip: classes.noteByDriver }}
            title={
              <>
                {stop.recipient_note && (
                  <>
                    <Typography
                      component="h6"
                      className={classes.noteByDriverTitle}
                    >
                      Note for recipient
                    </Typography>
                    <Typography
                      component="p"
                      className={classes.noteByDriverText}
                    >
                      {stop.recipient_note}
                    </Typography>
                  </>
                )}
                {stop.completion_note && (
                  <>
                    <Typography
                      component="h6"
                      className={classes.noteByDriverTitle}
                    >
                      Note for internal use
                    </Typography>
                    <Typography
                      component="p"
                      className={classes.noteByDriverText}
                    >
                      {stop.completion_note}
                    </Typography>
                  </>
                )}
              </>
            }
          >
            <StickyNote className="stop-status-icon stop-status-icon--note" />
          </OptimizedTooltip>
        )}
        {!unassigned && (
          <>
            {!!isLate && !isDone && !isSkipped && (
              <OptimizedTooltip placement="bottom" title="Late">
                <LateIcon
                  fontSize="small"
                  className="stop-status-icon stop-status-icon--late"
                />
              </OptimizedTooltip>
            )}
            {!!isEarly && !isDone && !isSkipped && (
              <OptimizedTooltip placement="bottom" title="Early">
                <SoonerIcon fontSize="small" className="stop-status-icon" />
              </OptimizedTooltip>
            )}
            {!!isDone && (
              <OptimizedTooltip placement="bottom" title="Done">
                <CheckIcon
                  fontSize="small"
                  className="stop-status-icon stop-status-icon--done"
                />
              </OptimizedTooltip>
            )}
            {!!isSkipped && (
              <OptimizedTooltip placement="bottom" title="Skipped">
                <CloseIcon
                  fontSize="small"
                  className="stop-status-icon stop-status-icon--skipped"
                />
              </OptimizedTooltip>
            )}
          </>
        )}

        {!unassigned && (
          <Typography
            component="span"
            variant="body2"
            className={clsx({
              [classes.stopInfoTimeLabel]: isSkipped || isDone,
              [classes.stopInfoTimeLabelSkipped]: isSkipped,
              [classes.stopInfoTimeLabelDone]: isDone
            })}
          >
            {!unassigned && (isSkipped || isDone) && !hideDetails
              ? secondsToTime(stop.finish_time)
              : stop.type !== "start"
              ? secondsToTime(arr_time)
              : secondsToTime(end_time)}
          </Typography>
        )}
        {unassigned && <br />}
      </Typography>
      <Typography className={classes.text13} color="secondary">
        {!noTimeWindow &&
          (Number.isInteger(timeWindowEarliest) &&
          Number.isInteger(timeWindowLatest)
            ? `${secondsToTime(timeWindowEarliest)}-${secondsToTime(
                timeWindowLatest
              )}`
            : "")}
      </Typography>
    </>
  );
}
